import { IconButton } from "@mui/material"
import { Check, X } from "lucide-mui"
import { DateTime } from "luxon"
import React, { useMemo } from "react"

import i18n from "@/i18n"

import MobileAvailabilityTimeline, { type TimelineBusyBlock, type TimelineWindow } from "./mobile_availability_timeline"

type TableTimelineProps = {
  tableName: string
  timeOptions: [string, DateTime][]
  busyBlocks: TimelineBusyBlock[]
  closedUntil?: DateTime | null
  /** Confirmed guest window (both time & table set). */
  currentReservation?: { startTime: string; endTime: string }
  /** Exact-minute opening bounds for the date; threaded to the strip so its range matches step 1. */
  openingRange?: { opensAt: DateTime; closesAt: DateTime } | null
  /** Earliest free window for the selected table, shown as the green proposal when no time is set. */
  proposedWindow?: TimelineWindow | null
  /** True when the confirmed window collides with a busy block — drives the collision error block. */
  hasConflict: boolean
  onConflictChange: (hasConflicts: boolean) => void
  onAcceptProposed: () => void
  onDeselect: () => void
}

const STYLES = {
  // No top border: the footer wrapper owns the sheet's rounded top + up-shadow (Figma 1283-6812).
  container: "",
  header: "flex items-center justify-between px-5 pt-3",
  title: "text-[14px] font-medium text-[#171717]",
  conflict: "border-t border-[#e8e8ed] px-5 pb-2 pt-3",
  conflictMessage: "text-[12px] leading-5 text-black",
  conflictList: "mt-0.5 flex flex-col",
  conflictSuggestion: "flex items-center gap-1.5",
  conflictCheck: "h-3 w-3 shrink-0 text-[#2596fa]",
  conflictSuggestionText: "text-[10px] leading-5 text-[#818793]",
} as const

/** The three advisory lines (blue check + grey text) shown under the collision headline (Figma 1283-7658). */
const CONFLICT_SUGGESTION_KEYS = [
  "conflict_suggestion_time",
  "conflict_suggestion_table",
  "conflict_suggestion_duration",
] as const

/**
 * Selected-table availability panel (Figma 1283-6812 / collision 1283-7658): a "Table {name}" header
 * with a deselect (×), above the display-only {@link MobileAvailabilityTimeline} strip. The strip
 * renders the blue/green/red states and reports conflicts up via `onConflictChange` (disables the CTA).
 */
const TableTimeline: React.FC<TableTimelineProps> = ({
  tableName,
  timeOptions,
  busyBlocks,
  closedUntil,
  currentReservation,
  openingRange,
  proposedWindow,
  hasConflict,
  onConflictChange,
  onAcceptProposed,
  onDeselect,
}) => {
  const confirmedWindow = useMemo<TimelineWindow | null>(
    () =>
      currentReservation
        ? {
            // `setZone: true` keeps the venue offset carried by the ISO string (device tz ≠ venue tz).
            start: DateTime.fromISO(currentReservation.startTime, { setZone: true }),
            end: DateTime.fromISO(currentReservation.endTime, { setZone: true }),
          }
        : null,
    [currentReservation?.startTime, currentReservation?.endTime],
  )

  return (
    <div className={STYLES.container} data-testid="public-reservation-modal-mobile-timeline">
      <div className={STYLES.header}>
        <span className={STYLES.title}>{tableName}</span>
        <IconButton
          size="small"
          aria-label={i18n.t("app.public.venue.modal.clear_table_selection")}
          onClick={onDeselect}
          sx={{ padding: "2px" }}
          data-testid="public-reservation-modal-mobile-timeline-deselect"
        >
          <X className="h-4 w-4 text-[#5f6774]" />
        </IconButton>
      </div>

      {hasConflict ? (
        <div className={STYLES.conflict} data-testid="public-reservation-modal-mobile-conflict">
          <p className={STYLES.conflictMessage} data-testid="public-reservation-modal-mobile-conflict-message">
            {i18n.t("app.public.venue.table_selection.conflict_message")}
          </p>
          <ul className={STYLES.conflictList}>
            {CONFLICT_SUGGESTION_KEYS.map((key, index) => (
              <li
                key={key}
                className={STYLES.conflictSuggestion}
                data-testid={`public-reservation-modal-mobile-conflict-suggestion-${index}`}
              >
                <Check className={STYLES.conflictCheck} />
                <span className={STYLES.conflictSuggestionText}>
                  {i18n.t(`app.public.venue.table_selection.${key}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <MobileAvailabilityTimeline
        timeOptions={timeOptions}
        openingRange={openingRange}
        busyBlocks={busyBlocks}
        closedUntil={closedUntil}
        confirmedWindow={confirmedWindow}
        proposedWindow={proposedWindow}
        onConflictChange={onConflictChange}
        onAcceptProposed={onAcceptProposed}
      />
    </div>
  )
}

export default TableTimeline
